import {useQuery, useQueryClient} from "@tanstack/react-query";
import {getUserDetail} from "api/auth.api.ts";
import {createContext, useContext, useEffect, useState} from "react";
import {safeLocalStorage} from "utils";
import {auth} from "services/firebase";
import {onAuthStateChanged, signInWithCustomToken, signOut,} from "firebase/auth";

// Set staleTime and cacheTime to 24 hours (1000 * 60 * 60 * 24)
//
// This prevents unnecessary refetching of user data, which could trigger
// initData in both create and update instructor flows.
//
// Since user data changes infrequently and is explicitly invalidated
// when updated, this improves performance and reduces redundant network requests.

// TODO Find a better solution if possible
const staleTime = 1000 * 60 * 60 * 24;
const cacheTime = 1000 * 60 * 60 * 24;
const AuthContext = createContext({});

/**
 * Represents the role of the authenticated user.
 *
 * - `1` → Regular User
 * - `2` → Instructor
 *
 */
export const AuthProvider = ({children}) => {
    const queryClient = useQueryClient();
    const [token, setToken] = useState(() => safeLocalStorage.getItem("token", null));
    const [firebaseUser, setFirebaseUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [userRole, setUserRole] = useState(1);

    useEffect
    (() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            setFirebaseUser(user || null);
            setAuthReady(true);
        });
        return () => unsub();
    }, []);

    const {
        data: userData,
        isLoading,
        fetchStatus,
        refetch,
    } = useQuery({
        queryKey: ["userDetail"],
        queryFn: getUserDetail,
        enabled: authReady && !!token,
        staleTime,
        cacheTime,
    });

    const updateAuthData = (nextToken) => {

        if (!nextToken) {
            safeLocalStorage.removeItem("token");
            return
        }
        setToken(nextToken);
        safeLocalStorage.setItem("token", nextToken);
    };

    const loginWithCustomToken = async ({firebaseToken, appToken}) => {
        await signInWithCustomToken(auth, firebaseToken);
        if (appToken) updateAuthData(appToken);
        return auth.currentUser;
    };

    const toggleRole = () => setUserRole((prev) => (prev === 2 ? 1 : 2));

    const logout = async () => {
        queryClient.removeQueries();
        setToken(null);
        setAuthReady(false);
        setUserRole(1);
        safeLocalStorage.clear();
        try {
            await signOut(auth);
        } catch (e) {
            console.error({firebase_signOut_error: e});
        }

    };

    return (
        <AuthContext.Provider
            value={{
                firebaseUser,
                authReady,
                userData,
                token,
                updateAuthData,
                loginWithCustomToken,
                userRole,
                setUserRole,
                toggleRole,
                logout,
                isLoading: (isLoading && fetchStatus !== "idle") || !authReady,
                refetch,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};
